import { type AnyAction, combineReducers } from 'redux';
import Immutable from 'immutable';

import { type PublicVideo, VideoStatus } from 'js/src/client';
import {
    setLoadingFinished,
    setVideoName,
    setQueuedArtifactDownload,
    setVideoStatus,
    setVideoVisibility,
    startAutoDownload,
    storeRenderEstimate,
    storeVideo,
} from 'js/src/data/share-video/share-video-actions';
import type { PublicVideoRenderEstimate } from 'js/src/data/share-video/share-video-types';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';

export function video(
    state = Immutable.Map() as unknown as ImmutableMap<PublicVideo>,
    action: AnyAction,
) {
    if (storeVideo.match(action)) {
        return ImmutableFromJS(action.payload);
    }
    if (setVideoStatus.match(action)) {
        return state.set('status', action.payload);
    }
    if (setVideoVisibility.match(action)) {
        return state.set('visibility', action.payload);
    }
    if (setVideoName.match(action)) {
        return state.set('name', action.payload);
    }
    return state;
}

export function renderEstimate(
    state: ImmutableMap<PublicVideoRenderEstimate> | null = null,
    action: AnyAction,
) {
    if (storeRenderEstimate.match(action)) {
        return ImmutableFromJS(action.payload);
    }
    return state;
}

export function loading(state = true, action: AnyAction) {
    if (setLoadingFinished.match(action)) {
        return false;
    }
    return state;
}

export function autoDownload(state = false, action: AnyAction) {
    if (startAutoDownload.match(action)) {
        return true;
    }
    return state;
}

export function queuedArtifactDownload(state: string | null = null, action: AnyAction) {
    if (setQueuedArtifactDownload.match(action)) {
        return action.payload;
    }
    return state;
}

/**
 * Whether the stored video carries render output (the per-asset `renderUrl`s) and not just a
 * PUBLISHED status. `setVideoStatus` flips the status a network round-trip before the refetched
 * video lands, so anything reading rendered media has to wait for the `storeVideo` that follows.
 */
export function renderDataLoaded(state = false, action: AnyAction) {
    if (storeVideo.match(action)) {
        return ImmutableFromJS(action.payload).get('status') === VideoStatus.PUBLISHED;
    }
    if (setVideoStatus.match(action)) {
        return state && action.payload === VideoStatus.PUBLISHED;
    }
    return state;
}

export const shareVideo = combineReducers({
    video,
    renderEstimate,
    loading,
    autoDownload,
    queuedArtifactDownload,
    renderDataLoaded,
});

export type ShareVideoState = ReturnType<typeof shareVideo>;
